import React from 'react';
import Icon from '../../../components/AppIcon';

const BusinessPreview = ({ formData }) => {
  if (!formData?.businessName) {
    return (
      <div className="bg-card rounded-xl p-6 border border-border">
        <div className="flex items-center justify-center h-32 text-muted-foreground">
          <div className="text-center">
            <Icon name="Building2" size={24} className="mx-auto mb-2 opacity-50" />
            <p className="text-sm">Business preview will appear here</p>
          </div>
        </div>
      </div>
    );
  }

  const formatOpeningHours = () => {
    const hours = formData?.location?.openingHours;
    if (!hours) return 'Hours not specified';
    
    // Fix the date formatting error by using 'long' format then converting to lowercase
    const todayKey = new Date()?.toLocaleDateString('en-US', { weekday: 'long' })?.toLowerCase();
    const todayHours = hours?.[todayKey];
    
    if (todayHours && todayHours?.toLowerCase() !== 'closed') {
      return `Open today: ${todayHours}`;
    }
    
    // Find next open day
    const daysOrder = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    const today = daysOrder?.indexOf(todayKey);
    
    for (let i = 1; i <= 7; i++) {
      const nextDayIndex = (today + i) % 7;
      const nextDay = daysOrder?.[nextDayIndex];
      const nextDayHours = hours?.[nextDay];
      
      if (nextDayHours && nextDayHours?.toLowerCase() !== 'closed') {
        const dayName = nextDay?.charAt(0)?.toUpperCase() + nextDay?.slice(1);
        return `Opens ${dayName}: ${nextDayHours}`;
      }
    }
    
    return 'Hours not available';
  };

  return (
    <div className="bg-card rounded-xl border border-border overflow-hidden">
      {/* Hero Image */}
      {formData?.heroImagePreview && (
        <div className="h-32 bg-gradient-to-r from-primary/20 to-primary/10 relative">
          <img
            src={formData?.heroImagePreview}
            alt="Business hero"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/20"></div>
        </div>
      )}
      {/* Business Header */}
      <div className="p-4">
        <div className="flex items-start space-x-3">
          {/* Logo */}
          <div className="w-12 h-12 bg-muted rounded-lg flex items-center justify-center flex-shrink-0">
            {formData?.logoPreview ? (
              <img
                src={formData?.logoPreview}
                alt="Business logo"
                className="w-full h-full object-cover rounded-lg"
              />
            ) : (
              <Icon name="Building2" size={20} className="text-muted-foreground" />
            )}
          </div>

          {/* Business Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-heading font-semibold text-foreground text-lg leading-tight">
                  {formData?.businessName}
                </h3>
                {formData?.category && (
                  <p className="text-sm text-muted-foreground capitalize">
                    {formData?.category?.replace('-', ' ')}
                  </p>
                )}
                
                {/* Rating */}
                {formData?.rating && (
                  <div className="flex items-center space-x-1 mt-1">
                    <div className="flex items-center">
                      {[1, 2, 3, 4, 5]?.map((star) => (
                        <Icon
                          key={star}
                          name="Star"
                          size={12}
                          className={
                            star <= Math.floor(formData?.rating)
                              ? "text-yellow-400 fill-current" :"text-muted-foreground"
                          }
                        />
                      ))}
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {formData?.rating} ({formData?.reviewCount || 0} reviews)
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* About Section */}
        {formData?.businessDescription && (
          <div className="mt-4">
            <h4 className="font-medium text-foreground text-sm mb-2">About</h4>
            <p className="text-sm text-muted-foreground line-clamp-3">
              {formData?.businessDescription}
            </p>
          </div>
        )}

        {/* Key Features */}
        {formData?.keyFeatures?.length > 0 && (
          <div className="mt-4">
            <h4 className="font-medium text-foreground text-sm mb-2">Key Features</h4>
            <div className="space-y-1">
              {formData?.keyFeatures?.slice(0, 3)?.map((feature, index) => (
                <div key={index} className="flex items-center space-x-2">
                  <Icon name="Check" size={12} className="text-green-500" />
                  <span className="text-xs text-muted-foreground">{feature}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Certifications */}
        {formData?.certifications?.length > 0 && (
          <div className="mt-4">
            <h4 className="font-medium text-foreground text-sm mb-2">Certifications</h4>
            <div className="flex flex-wrap gap-1">
              {formData?.certifications?.slice(0, 2)?.map((cert, index) => (
                <span 
                  key={index}
                  className="text-xs bg-primary/10 text-primary px-2 py-1 rounded-full"
                >
                  {cert?.name || 'Certification'}
                </span>
              ))}
              {formData?.certifications?.length > 2 && (
                <span className="text-xs text-muted-foreground px-2 py-1">
                  +{formData?.certifications?.length - 2} more
                </span>
              )}
            </div>
          </div>
        )}

        {/* Contact Information */}
        <div className="mt-4 pt-4 border-t border-border space-y-2">
          {/* Location */}
          {(formData?.location?.city || formData?.location?.postcode) && (
            <div className="flex items-center space-x-2">
              <Icon name="MapPin" size={14} className="text-muted-foreground" />
              <span className="text-sm text-muted-foreground">
                {formData?.location?.city}
                {formData?.location?.postcode && `, ${formData?.location?.postcode}`}
              </span>
            </div>
          )}

          {/* Phone */}
          {formData?.contact?.phone && (
            <div className="flex items-center space-x-2">
              <Icon name="Phone" size={14} className="text-muted-foreground" />
              <span className="text-sm text-muted-foreground">
                {formData?.contact?.phone}
              </span>
            </div>
          )}

          {/* Email */}
          {formData?.contact?.email && (
            <div className="flex items-center space-x-2">
              <Icon name="Mail" size={14} className="text-muted-foreground" />
              <span className="text-sm text-muted-foreground">
                {formData?.contact?.email}
              </span>
            </div>
          )}

          {/* Website */}
          {formData?.contact?.website && (
            <div className="flex items-center space-x-2">
              <Icon name="Globe" size={14} className="text-muted-foreground" />
              <a
                href={formData?.contact?.website?.startsWith('http') ? formData?.contact?.website : `https://${formData?.contact?.website}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-primary hover:text-primary/80 transition-colors"
                onClick={(e) => e?.stopPropagation()}
              >
                {formData?.contact?.website?.replace(/^https?:\/\//, '')}
              </a>
            </div>
          )}

          {/* Opening Hours */}
          <div className="flex items-center space-x-2">
            <Icon name="Clock" size={14} className="text-muted-foreground" />
            <span className="text-sm text-muted-foreground">
              {formatOpeningHours()}
            </span>
          </div>
        </div>

        {/* Social Media Links */}
        {(formData?.socialMedia?.facebook || formData?.socialMedia?.instagram || 
          formData?.socialMedia?.x || formData?.socialMedia?.linkedin) && (
          <div className="mt-4 pt-4 border-t border-border">
            <h4 className="font-medium text-foreground text-sm mb-2">Social Media</h4>
            <div className="flex items-center space-x-3">
              {formData?.socialMedia?.facebook && (
                <a
                  href={formData?.socialMedia?.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 border border-black rounded-lg hover:bg-muted/50 transition-colors"
                  onClick={(e) => e?.stopPropagation()}
                >
                  <Icon name="Facebook" size={16} className="text-black" />
                </a>
              )}
              {formData?.socialMedia?.instagram && (
                <a
                  href={formData?.socialMedia?.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 border border-black rounded-lg hover:bg-muted/50 transition-colors"
                  onClick={(e) => e?.stopPropagation()}
                >
                  <Icon name="Instagram" size={16} className="text-black" />
                </a>
              )}
              {formData?.socialMedia?.x && (
                <a
                  href={formData?.socialMedia?.x}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 border border-black rounded-lg hover:bg-muted/50 transition-colors"
                  onClick={(e) => e?.stopPropagation()}
                >
                  <Icon name="Twitter" size={16} className="text-black" />
                </a>
              )}
              {formData?.socialMedia?.linkedin && (
                <a
                  href={formData?.socialMedia?.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 border border-black rounded-lg hover:bg-muted/50 transition-colors"
                  onClick={(e) => e?.stopPropagation()}
                >
                  <Icon name="Linkedin" size={16} className="text-black" />
                </a>
              )}
            </div>
          </div>
        )}

        {/* Features */}
        {Object.values(formData?.features || {})?.some(Boolean) && (
          <div className="mt-3 flex flex-wrap gap-1">
            {formData?.features?.parkingAvailable && (
              <span className="text-xs bg-muted px-2 py-1 rounded-full text-muted-foreground">
                Parking
              </span>
            )}
            {formData?.features?.wheelchairAccessible && (
              <span className="text-xs bg-muted px-2 py-1 rounded-full text-muted-foreground">
                Accessible
              </span>
            )}
            {formData?.features?.onlineBooking && (
              <span className="text-xs bg-muted px-2 py-1 rounded-full text-muted-foreground">
                Online Booking
              </span>
            )}
          </div>
        )}

        {/* Completion Status */}
        <div className="mt-4 pt-3 border-t border-border">
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">Profile Completion</span>
            <div className="flex items-center space-x-1">
              {[1, 2, 3]?.map((step) => {
                const isCompleted = step === 1 && (formData?.businessName && formData?.category) ||
                                  step === 2 && (formData?.location?.city && formData?.contact?.phone) ||
                                  step === 3;
                
                return (
                  <div
                    key={step}
                    className={`w-2 h-2 rounded-full ${
                      isCompleted ? 'bg-primary' : 'bg-muted'
                    }`}
                  />
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BusinessPreview;